import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useSubmitOrder } from '@hooks/useSubmitOrder';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { useSessionNavigation } from '@hooks/useSessionNavigation';
import { useCheckoutStore } from '@store/checkoutStore';
import { selectQualifiedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { ROUTES } from '@constants/routes';
import { CPQ_ORDER_DATA_KEY } from '@t/session.types';
import type { ShoppingCartDTO } from '@t/api.types';

interface UsePlaceOrderParams {
  sessionId: string | null;
  cart: ShoppingCartDTO | null | undefined;
}

interface OrderData {
  orderId?: string;
  orderNumber?: string;
  workOrderId?: string;
}

export const usePlaceOrder = ({ sessionId, cart }: UsePlaceOrderParams) => {
  const navigate = useNavigate();
  const reset = useCheckoutStore((s) => s.reset);
  const [orderError, setOrderError] = useState(false);
  const { resolvedAddressId, qualifiedAddressId } = useQualificationFlowStore(
    useShallow((s) => ({
      resolvedAddressId: s.resolvedAddressId,
      qualifiedAddressId: selectQualifiedAddressId(s),
    }))
  );
  const analytics = useAnalytics();

  const { goToStep, isPending: isNavigating } = useSessionNavigation(sessionId ?? '');

  const {
    submit,
    isPending: isSubmitting,
    reset: resetSubmit,
  } = useSubmitOrder(sessionId ?? '', {
    onSuccess: (data) => {
      if (sessionId && cart) {
        analytics.fire('trackOrderSubmitted', {
          sessionId,
          cart,
          qualification: { qualifiedAddressId, resolvedAddressId },
          promoCode: undefined,
        });
        analytics.clearLedger();
      }
      const orderData = data as OrderData | undefined;
      const orderState = {
        orderNumber: orderData?.orderNumber ?? orderData?.orderId ?? '',
        orderId: orderData?.orderId ?? '',
        shoppingCartId: cart?.id ?? '',
        sessionId: sessionId ?? '',
      };
      localStorage.setItem(CPQ_ORDER_DATA_KEY, JSON.stringify(orderState));
      reset();
      navigate(ROUTES.DONE, { state: orderState });
    },
  });

  const placeOrder = async (): Promise<void> => {
    resetSubmit();
    setOrderError(false);
    try {
      const navResult = await goToStep('CONFIRMATION');
      if (!navResult.valid) {
        setOrderError(true);
        return;
      }
      submit(undefined, {
        onError: () => setOrderError(true),
      });
    } catch {
      setOrderError(true);
    }
  };

  return {
    placeOrder,
    isSubmitting,
    isNavigating,
    orderError,
    clearOrderError: () => setOrderError(false),
  };
};
